import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import CSDTable from "@cloudscape-design/components-themed/table";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import {
  Box,
  SpaceBetween,
  Header,
  ColumnLayout,
  Button,
  Input,
  Select,
  SelectProps,
  FormField,
  Container,
  Link,
  Spinner,
  ExpandableSection,
} from "@cloudscape-design/components-themed";
import { COMMON_LABELS, addBlank } from "../default-constants";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import type { RootState } from "../../app/store";
import { LABELS } from "./constants";
import {
  useGetMerchantInfoInquiryStoreQuery,
  useGetMerchantStoreSiteIdQuery,
  useGetMerchantStoreStoreNameQuery,
  useLazyGetMerchantStoreListDownloadQuery,
} from "../../features/api";
import {
  isHalfNumberAndCharValid,
  isLengthValid,
} from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import { createSearchCondition } from "../../features/slices/store-tab-search";
import InfoStoreModal from "./InfoStoreModal";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { IconButton } from "../UI/button/IconButton";
import { getMerchantStoreNameItems, getMerchantStoreSiteIdItems } from "./util";
import { showModal } from "../UI/modal/modal-slice";

interface StoreTabProps {
  companyCode: string;
}

export const StoreTab = ({ companyCode }: StoreTabProps) => {
  const dispatch = useAppDispatch();

  const columnDefinitions = [
    {
      id: "status",
      header: LABELS.STATUS,
      cell: (e: any) => e.status,
    },
    {
      id: "tradeName",
      header: LABELS.MERCHANT_NAME,
      cell: (e: any) => e.tradeName,
    },
    {
      id: "cpid",
      header: LABELS.SITE_ID,
      cell: (e: any) => e.cpid,
    },
    {
      id: "businessCode",
      header: LABELS.CORP_CODE,
      cell: (e: any) => e.businessCode,
    },
    {
      id: "shopCode",
      header: LABELS.STORE_CODE,
      cell: (e: any) => (
        <Link
          onFollow={() => {
            setStoreNo(e.storeNo);
            setVisible(true);
          }}
        >
          {e.shopCode}
        </Link>
      ),
    },
    {
      id: "storeName",
      header: LABELS.STORE_NAME,
      cell: (e: any) => e.storeName,
    },
  ];

  const [isSkipSearch, setIsSkipSearch] = useState(true);

  // 検索条件取得
  const searchCondition = useAppSelector(
    (state: RootState) => state.merchantStoreSearchCondition
  );

  // データの取得
  const { data, isFetching } = useGetMerchantInfoInquiryStoreQuery(
    searchCondition,
    {
      skip: isSkipSearch,
    }
  );

  // サイトID一覧取得
  const { data: merchantStoreSiteId } =
    useGetMerchantStoreSiteIdQuery(companyCode);
  // 加盟店一覧取得
  const { data: MerchantStoreStoreName } =
    useGetMerchantStoreStoreNameQuery(companyCode);

  // サイトID
  const [selectedSiteIdOption, setSelectedSiteIdOption] = useState({
    value: "",
  });

  // 加盟店名
  const [selectedStoreNameOption, setSelectedStoreNameOption] = useState({
    value: "",
  });

  // サイトID一覧
  const [merchantStoreSiteIdList, setMerchantStoreSiteIdList] =
    useState<SelectProps.Options>();

  // 加盟店名
  const [merchantStoreNameList, setMerchantStoreNameList] =
    useState<SelectProps.Options>();

  //店舗No
  const [storeNo, setStoreNo] = useState<number>(0);

  // 企業コード
  const [businessCode, setBusinessCode] = useState("");
  const [businessCodeErrorMsg, setBusinessCodeErrorMsg] = useState("");

  // 店舗コード
  const [shopCode, setShopCode] = useState("");
  const [shopCodeErrorMsg, setShopCodeErrorMsg] = useState("");

  // 店舗名
  const [storeName, setStoreName] = useState("");
  const [storeNameErrorMsg, setStoreNameErrorMsg] = useState("");

  // 登録事業者 法人番号
  const [registeredCompanyCorpNo, setRegisteredCompanyCorpNo] = useState("");
  const [registeredCompanyCorpNoErrorMsg, setRegisteredCompanyCorpNoErrorMsg] =
    useState("");

  // 登録事業者名
  const [registeredCompanyCompanyName, setRegisteredCompanyCompanyName] =
    useState("");
  const [
    registeredCompanyCompanyNameErrorMsg,
    setRegisteredCompanyCompanyNameErrorMsg,
  ] = useState("");

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });
  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  const [visible, setVisible] = useState(false);

  // spinner・文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] =
    useState<boolean>(false);
  // button表示有無
  const [
    isMerchantStoreListDownloadButton,
    setIsMerchantStoreListDownloadButton,
  ] = useState<boolean>(false);

  //モーダル閉じる関数
  const closeDetailModal = () => {
    setVisible(false);
  };

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (allItems) {
      setPagedItems(allItems.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  useEffect(() => {
    setMerchantStoreSiteIdList(
      addBlank(getMerchantStoreSiteIdItems(merchantStoreSiteId))
    );
  }, [merchantStoreSiteId]);

  useEffect(() => {
    setMerchantStoreNameList(
      addBlank(getMerchantStoreNameItems(MerchantStoreStoreName))
    );
  }, [MerchantStoreStoreName]);

  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, preferences.pageSize));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ダウンロード関連変数
  const [lazyDownloadMerchantStoreListTrigger] =
    useLazyGetMerchantStoreListDownloadQuery();

  const handleOnSubmit = (e: any) => {
    // イベントキャンセル
    e.preventDefault();

    // バリデーションチェック
    const result = validateInputs();

    if (result) {
      setIsSkipSearch(false);
      dispatch(
        createSearchCondition({
          companyCode,
          cpid: selectedSiteIdOption.value,
          businessCode,
          shopCode,
          tradeName: selectedStoreNameOption.value,
          storeName,
          timestamp: new Date().getTime(),
          registeredCompanyCorpNo,
          registeredCompanyCompanyName,
        })
      );
    }
  };

  // 店舗名バリデーションチェック
  const checkStoreName = (storeName: string) => {
    // 桁数チェック
    if (!isLengthValid(storeName, LABELS.STORE_NAME_LEN)) {
      setStoreNameErrorMsg(
        getMessage("KGRERR058", LABELS.STORE_NAME_LEN).label
      );
      return false;
    }
    // エラーを消す
    setStoreNameErrorMsg("");

    return true;
  };

  // 店舗コードバリデーションチェック
  const checkShopCode = (shopCode: string) => {
    // 桁数チェック
    if (
      !isLengthValid(shopCode, LABELS.SHOP_CODE_LEN) ||
      !isHalfNumberAndCharValid(shopCode)
    ) {
      setShopCodeErrorMsg(
        getMessage(
          "KGRERR065",
          LABELS.HALF_WIDTH_ALPHANUMERIC,
          LABELS.SHOP_CODE_LEN
        ).label
      );
      return false;
    }
    // エラーを消す
    setShopCodeErrorMsg("");

    return true;
  };

  // 企業コードバリデーションチェック
  const checkBusinessCode = (businessCode: string) => {
    // 桁数チェック
    if (
      !isLengthValid(businessCode, LABELS.BUSINESS_CODE_LEN) ||
      !isHalfNumberAndCharValid(businessCode)
    ) {
      setBusinessCodeErrorMsg(
        getMessage(
          "KGRERR065",
          LABELS.HALF_WIDTH_ALPHANUMERIC,
          LABELS.BUSINESS_CODE_LEN
        ).label
      );
      return false;
    }
    // エラーを消す
    setBusinessCodeErrorMsg("");

    return true;
  };

  // 登録事業者法人番号 法人番号バリデーションチェック
  const checkRegisteredCompanyCorpNo = (registeredCompanyCorpNo: string) => {
    // 桁数チェック
    if (
      !isLengthValid(
        registeredCompanyCorpNo,
        LABELS.REGISTERED_COMPANY_CORPORATE_NUMBER_LEN
      )
    ) {
      setRegisteredCompanyCorpNoErrorMsg(
        getMessage(
          "KGRERR065",
          LABELS.HALF_WIDTH_ALPHANUMERIC,
          LABELS.REGISTERED_COMPANY_CORPORATE_NUMBER_LEN
        ).label
      );
      return false;
    }
    // エラーを消す
    setRegisteredCompanyCorpNoErrorMsg("");

    return true;
  };

  // 登録事業者名バリデーションチェック
  const checkRegisteredCompanyCompanyName = (
    registeredCompanyCompanyName: string
  ) => {
    // 桁数チェック
    if (
      !isLengthValid(
        registeredCompanyCompanyName,
        LABELS.REGISTERED_COMPANY_COMPANY_NAME_LEN
      )
    ) {
      setRegisteredCompanyCompanyNameErrorMsg(
        getMessage("KGRERR058", LABELS.REGISTERED_COMPANY_COMPANY_NAME_LEN)
          .label
      );
      return false;
    }
    // エラーを消す
    setRegisteredCompanyCompanyNameErrorMsg("");

    return true;
  };

  // 入力チェック
  const validateInputs = () => {
    if (!checkStoreName(storeName)) {
      return false;
    }
    if (!checkShopCode(shopCode)) {
      return false;
    }
    if (!checkBusinessCode(businessCode)) {
      return false;
    }
    if (!checkRegisteredCompanyCorpNo(registeredCompanyCorpNo)) {
      return false;
    }

    if (!checkRegisteredCompanyCompanyName(registeredCompanyCompanyName)) {
      return false;
    }

    return true;
  };

  const onClickStoreDownloadButton = (e: CustomEvent<ClickDetail>) => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.DL_WAIT,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          e.preventDefault();
          executeDownload();
        },
      })
    );
  };

  const executeDownload = async () => {
    // spinner・文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsMerchantStoreListDownloadButton(true);
    await lazyDownloadMerchantStoreListTrigger(searchCondition)
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsMerchantStoreListDownloadButton(false);
      });
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container>
        <Box variant="strong" color="text-status-error">
          {LABELS.SEARCH_ANNOTATION_MESSAGE}
        </Box>
        <SpaceBetween direction="vertical" size="l">
          <ColumnLayout columns={4}>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.SITE_ID}</Box>
                </SpaceBetween>
              }
              stretch={true}
            >
              <Select
                selectedOption={selectedSiteIdOption}
                onChange={({ detail }: { detail: any }) =>
                  setSelectedSiteIdOption(detail.selectedOption)
                }
                options={merchantStoreSiteIdList}
                filteringType="auto"
              />
            </FormField>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.CORP_CODE}</Box>
                </SpaceBetween>
              }
              stretch={true}
              errorText={businessCodeErrorMsg}
              constraintText={
                LABELS.HALF_WIDTH_ALPHANUMERIC +
                LABELS.BUSINESS_CODE_LEN +
                LABELS.CONSTRAINT_TEXT_DIGITS
              }
            >
              <Input
                value={businessCode}
                onChange={(event) => {
                  setBusinessCode(event.detail.value);
                  checkBusinessCode(event.detail.value);
                }}
              />
            </FormField>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.STORE_CODE}</Box>
                </SpaceBetween>
              }
              stretch={true}
              errorText={shopCodeErrorMsg}
              constraintText={
                LABELS.HALF_WIDTH_ALPHANUMERIC +
                LABELS.SHOP_CODE_LEN +
                LABELS.CONSTRAINT_TEXT_DIGITS
              }
            >
              <Input
                value={shopCode}
                onChange={(event) => {
                  setShopCode(event.detail.value);
                  checkShopCode(event.detail.value);
                }}
              />
            </FormField>
          </ColumnLayout>
          <ColumnLayout columns={4}>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.MERCHANT_NAME}</Box>
                </SpaceBetween>
              }
              stretch={true}
            >
              <Select
                selectedOption={selectedStoreNameOption}
                onChange={({ detail }: { detail: any }) =>
                  setSelectedStoreNameOption(detail.selectedOption)
                }
                options={merchantStoreNameList}
                filteringType="auto"
              />
            </FormField>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.STORE_NAME}</Box>
                </SpaceBetween>
              }
              stretch={true}
              errorText={storeNameErrorMsg}
              constraintText={LABELS.STORE_NAME_LEN + "文字以内"}
            >
              <Input
                value={storeName}
                onChange={(event) => {
                  setStoreName(event.detail.value);
                  checkStoreName(event.detail.value);
                }}
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      <Container>
        <ExpandableSection headerText={LABELS.DETAIL_SEARCH}>
          <ColumnLayout columns={2}>
            <ColumnLayout columns={2}>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box variant="strong">
                      {LABELS.REGISTERED_COMPANY_CORPORATE_NUMBER}
                    </Box>
                    <Box variant="small">
                      {COMMON_LABELS.CONTENTS.COMPANY_LINKED_STORE}
                    </Box>
                  </SpaceBetween>
                }
                stretch={true}
                errorText={registeredCompanyCorpNoErrorMsg}
                constraintText={
                  LABELS.HALF_WIDTH_ALPHANUMERIC +
                  LABELS.REGISTERED_COMPANY_CORPORATE_NUMBER_LEN +
                  LABELS.CONSTRAINT_TEXT_DIGITS
                }
              >
                <Input
                  value={registeredCompanyCorpNo}
                  onChange={(event) => {
                    setRegisteredCompanyCorpNo(event.detail.value);
                    checkRegisteredCompanyCorpNo(event.detail.value);
                  }}
                />
              </FormField>
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box variant="strong">
                      {LABELS.REGISTERED_COMPANY_COMPANY_NAME}
                    </Box>
                    <Box variant="small">
                      {COMMON_LABELS.CONTENTS.COMPANY_LINKED_STORE}
                    </Box>
                  </SpaceBetween>
                }
                stretch={true}
                errorText={registeredCompanyCompanyNameErrorMsg}
                constraintText={
                  LABELS.REGISTERED_COMPANY_COMPANY_NAME_LEN + "文字以内"
                }
              >
                <Input
                  value={registeredCompanyCompanyName}
                  onChange={(event) => {
                    setRegisteredCompanyCompanyName(event.detail.value);
                    checkRegisteredCompanyCompanyName(event.detail.value);
                  }}
                />
              </FormField>
            </ColumnLayout>
          </ColumnLayout>
        </ExpandableSection>
      </Container>
      <ColumnLayout columns={2}>
        <Box float="left"></Box>
        <Box float="right">
          <Button formAction="none" variant="normal" onClick={handleOnSubmit}>
            {COMMON_LABELS.BTNS.SEARCH}
          </Button>
        </Box>
      </ColumnLayout>
      <CSDTable
        columnDefinitions={columnDefinitions}
        trackBy="id"
        items={pagedItems}
        resizableColumns
        stickyHeader
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        header={
          <SpaceBetween size="xs">
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {isDownloadSpinnerAndStrDisplay ? (
                    <Spinner size="big" />
                  ) : (
                    <></>
                  )}
                  {isDownloadSpinnerAndStrDisplay ? (
                    <>{COMMON_LABELS.CONTENTS.DL_RUN}</>
                  ) : (
                    <></>
                  )}
                  <IconButton
                    iconName={"download"}
                    label={COMMON_LABELS.BTNS.DL}
                    disabled={isMerchantStoreListDownloadButton}
                    onClick={onClickStoreDownloadButton}
                  />
                </SpaceBetween>
              }
            >
              {LABELS.STORE_TABLE}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={preferences.pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={preferences}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
      <InfoStoreModal
        visible={visible}
        closeDetailModal={closeDetailModal}
        storeNo={storeNo}
      />
    </SpaceBetween>
  );
};

export default StoreTab;
